// Look-alikes: geometry of grid regions, cuts and congruent pieces.
//
// A region is a set of unit cells [x, y] (y grows downwards). The player draws cuts along grid edges; the
// connected groups of cells that remain are the pieces. The puzzle is solved when there are exactly N pieces
// and all of them are the same shape (turning a piece or flipping it over is allowed).

export type Cell = readonly [number, number];

export const cellKey = (x: number, y: number): string => x + "," + y;
/** reading order: top to bottom, then left to right */
export const byReading = (a: Cell, b: Cell): number => a[1] - b[1] || a[0] - b[0];

/** Edge ids. "v3,2" is the wall on the left side of cell (3,2); "h3,2" the wall on top of cell (3,2). */
export const vEdge = (x: number, y: number): string => "v" + x + "," + y;
export const hEdge = (x: number, y: number): string => "h" + x + "," + y;

export function normalize(cells: readonly Cell[]): Cell[] {
  let mx = Infinity;
  let my = Infinity;
  for (const [x, y] of cells) {
    if (x < mx) mx = x;
    if (y < my) my = y;
  }
  return cells.map(([x, y]) => [x - mx, y - my] as Cell).sort(byReading);
}

/** t in 0..7: rotate by 90° * (t & 3), then mirror when t & 4 */
export function transform(cells: readonly Cell[], t: number): Cell[] {
  return cells.map(([x0, y0]) => {
    let x = x0;
    let y = y0;
    for (let i = 0; i < (t & 3); i++) [x, y] = [-y, x];
    if (t & 4) x = -x;
    return [x, y] as Cell;
  });
}

const serialize = (cells: readonly Cell[]): string => cells.map((c) => c[0] + "," + c[1]).join(";");

/** The distinct orientations (rotations and mirror images) of a shape, each normalized. */
export function orientations(cells: readonly Cell[]): Cell[][] {
  const seen = new Set<string>();
  const out: Cell[][] = [];
  for (let t = 0; t < 8; t++) {
    const o = normalize(transform(cells, t));
    const k = serialize(o);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(o);
    }
  }
  return out;
}

/** Same string for two shapes exactly when one can be turned/flipped into the other. */
export function shapeKey(cells: readonly Cell[]): string {
  let best = "";
  for (let t = 0; t < 8; t++) {
    const k = serialize(normalize(transform(cells, t)));
    if (best === "" || k < best) best = k;
  }
  return best;
}

/** Every wall the region allows: edges between two neighbouring cells of the region. */
export function interiorEdges(cells: readonly Cell[]): string[] {
  const set = new Set(cells.map(([x, y]) => cellKey(x, y)));
  const out: string[] = [];
  for (const [x, y] of cells) {
    if (set.has(cellKey(x - 1, y))) out.push(vEdge(x, y));
    if (set.has(cellKey(x, y - 1))) out.push(hEdge(x, y));
  }
  return out;
}

export interface PieceInfo {
  cells: Cell[];
  /** pieces with the same class index have the same shape */
  cls: number;
}

export interface Analysis {
  pieces: PieceInfo[];
  classes: number;
  solved: boolean;
}

/** Split the region along the cuts and compare the pieces. `cells` must be in reading order. */
export function analyze(cells: readonly Cell[], cuts: ReadonlySet<string>, n: number): Analysis {
  const index = new Map<string, number>();
  cells.forEach(([x, y], i) => index.set(cellKey(x, y), i));
  const seen = new Uint8Array(cells.length);
  const pieces: PieceInfo[] = [];
  const classOf = new Map<string, number>();
  for (let s = 0; s < cells.length; s++) {
    if (seen[s]) continue;
    const comp: Cell[] = [];
    const stack = [s];
    seen[s] = 1;
    while (stack.length) {
      const i = stack.pop()!;
      const [x, y] = cells[i];
      comp.push(cells[i]);
      const tryStep = (nx: number, ny: number, edge: string) => {
        if (cuts.has(edge)) return;
        const j = index.get(cellKey(nx, ny));
        if (j === undefined || seen[j]) return;
        seen[j] = 1;
        stack.push(j);
      };
      tryStep(x + 1, y, vEdge(x + 1, y));
      tryStep(x - 1, y, vEdge(x, y));
      tryStep(x, y + 1, hEdge(x, y + 1));
      tryStep(x, y - 1, hEdge(x, y));
    }
    comp.sort(byReading);
    const key = shapeKey(comp);
    let cls = classOf.get(key);
    if (cls === undefined) {
      cls = classOf.size;
      classOf.set(key, cls);
    }
    pieces.push({ cells: comp, cls });
  }
  return { pieces, classes: classOf.size, solved: pieces.length === n && classOf.size === 1 };
}

/** Edges that separate cells that belong to different pieces. */
export function cutsOfPartition(cells: readonly Cell[], pieceOf: ReadonlyMap<string, number>): string[] {
  const out: string[] = [];
  for (const [x, y] of cells) {
    const me = pieceOf.get(cellKey(x, y));
    const left = pieceOf.get(cellKey(x - 1, y));
    const up = pieceOf.get(cellKey(x, y - 1));
    if (left !== undefined && left !== me) out.push(vEdge(x, y));
    if (up !== undefined && up !== me) out.push(hEdge(x, y));
  }
  return out;
}

export function boundsOf(cells: readonly Cell[]): { minX: number; minY: number; maxX: number; maxY: number; w: number; h: number } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of cells) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

export function hasHoles(cells: readonly Cell[]): boolean {
  const b = boundsOf(cells);
  const set = new Set(cells.map(([x, y]) => cellKey(x, y)));
  // flood the outside (bounding box grown by one) and see whether every empty cell was reached
  const seen = new Set<string>();
  const stack: Cell[] = [[b.minX - 1, b.minY - 1]];
  seen.add(cellKey(b.minX - 1, b.minY - 1));
  while (stack.length) {
    const [x, y] = stack.pop()!;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = x + dx;
      const ny = y + dy;
      if (nx < b.minX - 1 || nx > b.maxX + 1 || ny < b.minY - 1 || ny > b.maxY + 1) continue;
      const k = cellKey(nx, ny);
      if (seen.has(k) || set.has(k)) continue;
      seen.add(k);
      stack.push([nx, ny]);
    }
  }
  const boxCells = (b.w + 2) * (b.h + 2);
  return seen.size + cells.length !== boxCells;
}

/** number of unit edges on the outline of the region */
export function perimeter(cells: readonly Cell[]): number {
  const set = new Set(cells.map(([x, y]) => cellKey(x, y)));
  let p = 0;
  for (const [x, y] of cells) {
    if (!set.has(cellKey(x + 1, y))) p++;
    if (!set.has(cellKey(x - 1, y))) p++;
    if (!set.has(cellKey(x, y + 1))) p++;
    if (!set.has(cellKey(x, y - 1))) p++;
  }
  return p;
}

/** the turns and flips (0..7) that carry the region exactly onto itself */
export function regionSymmetries(cells: readonly Cell[]): number[] {
  const base = serialize(normalize(cells));
  const out: number[] = [];
  for (let t = 0; t < 8; t++) if (serialize(normalize(transform(cells, t))) === base) out.push(t);
  return out;
}

/**
 * A name for a way of cutting the region into pieces: the same string for two cuttings that are turned or mirrored
 * copies of each other (using only the symmetries of the whole region), so "different ways" means really different.
 */
export function partitionKey(region: readonly Cell[], pieces: readonly (readonly Cell[])[], symmetries: readonly number[] = regionSymmetries(region)): string {
  let best = "";
  for (const t of symmetries) {
    const moved = transform(region, t);
    let mx = Infinity;
    let my = Infinity;
    for (const [x, y] of moved) {
      if (x < mx) mx = x;
      if (y < my) my = y;
    }
    const keys = pieces.map((piece) =>
      transform(piece, t).map(([x, y]) => [x - mx, y - my] as Cell).sort(byReading).map((c) => c[0] + "," + c[1]).join(";")
    );
    const key = keys.sort().join("|");
    if (best === "" || key < best) best = key;
  }
  return best;
}
